import {
    Button,
    Grid,
    Row,
    Col,
    Tabs,
    Tab,
    Table,
    FormGroup,
    FormControl,
    ControlLabel,
} from "react-bootstrap";
import AlertFlag from "../AlertFlag";
import AlertModal from "../AlertModal";
import FailedToLoadAlert from "../FailedToLoadAlert";
import ImageSetUploader from "../ImageSetUploader";
import ItemInfoTab from "../ItemInfoTab";
import LinkButton from "../LinkButton";
import Loader from "../Loader";
import React, { PureComponent } from "react";
import { StyleSheet, css } from "aphrodite";
import * as items from "../../lib/api/items";
import * as imageTypes from "../../enums/imageType";
import * as modifierTypes from "../../enums/modifierTypes";
import * as menuStatus from "../../enums/menuStatus";
import PropTypes from "prop-types";
import logger from "../../lib/logger";

const styles = StyleSheet.create({
    tabWrapper: {
        marginTop: 20,
    },
});

const strings = {
    loading: "Loading",
    titleSuffix: "| Habit CMS",
    item: "Item",
    info: "Info",
    images: "Images",
    isolatedImage128: "Rebrand Isolated Image 128",
    isolatedImage352: "Rebrand Isolated Image 352",
    isolatedImage1152: "Rebrand Isolated Image 1152",
    modifiers: "Modifiers",
    edit: "Edit",
    missingIsolatedImages128: "Missing Rebrand Isolated Image 128",
    missingIsolatedImages352: "Missing Rebrand Isolated Image 352",
    missingIsolatedImages1152: "Missing Rebrand Isolated Image 1152",
    name: "Name",
    available: "Available",
    sort: "Sort",
    manageSelections: "Manage Selections",
    type: "Type",
    disable: "Disable",
    enable: "Enable",
    yes: "Yes",
    no: "No",
    generalErrorMsg: "There was an error with your request.",
};

export default class ItemPage extends PureComponent {
    static propTypes = {
        match: PropTypes.object.isRequired,
    };

    state = {
        item: null,
        loading: true,
        currentModifierType: modifierTypes.ingredient.value,
        showAlertModal: false,
        errorMsg: "",
    };

    componentDidMount() {
        this._isMounted = true;
        document.title = `${strings.loading} ${strings.titleSuffix}`;
        items
            .getItem(this.props.match.params.itemId)
            .then(resp => {
                if (this._isMounted) {
                    this.setState({ item: resp, loading: false });
                    document.title = `${resp.name} ${strings.titleSuffix}`;
                }
            })
            .catch(this._handleRequestException);
    }

    componentWillUnmount() {
        this._isMounted = false;
    }

    _handleUploadImage = (imageType, image, crop) => {
        return items
            .addImage(this.state.item.id, imageType, image, crop)
            .then(images => {
                if (this._isMounted) {
                    this.setState({
                        item: {
                            ...this.state.item,
                            isolatedImages128:
                                imageType === imageTypes.Isolated128
                                    ? images
                                    : this.state.item.isolatedImages128,
                            isolatedImages352:
                                imageType === imageTypes.Isolated352
                                    ? images
                                    : this.state.item.isolatedImages352,
                            isolatedImages1152:
                                imageType === imageTypes.Isolated1152
                                    ? images
                                    : this.state.item.isolatedImages1152,
                        },
                    });
                }
                return images;
            })
            .catch(this._handleRequestException);
    };

    _handleModifierTypeChange = e => {
        this.setState({
            currentModifierType: e.target.value,
        });
    };

    _handleSaveInfo = item => {
        if (this._isMounted) {
            this.setState({ item });
        }
    };

    _toggleModifierStatus = modifier => {
        const newStatus =
            modifier.status === menuStatus.available.value
                ? menuStatus.unavailable.value
                : menuStatus.available.value;
        items
            .updateModifier(this.state.item.id, modifier.modifierId, newStatus)
            .then(() => {
                if (this._isMounted) {
                    this.setState({
                        item: {
                            ...this.state.item,
                            modifiers: this.state.item.modifiers.map(m => {
                                if (m.modifierId === modifier.modifierId) {
                                    return {
                                        ...m,
                                        status: newStatus,
                                    };
                                }
                                return m;
                            }),
                        },
                    });
                }
            })
            .catch(this._handleRequestException);
    };

    _reorderModifier = (modifier, newIndex) => {
        items
            .reorderModifier(this.state.item.id, modifier.modifierId, newIndex)
            .then(() => {
                if (this._isMounted) {
                    const currentModifiers = this.state.item.modifiers.filter(
                        m => m.type === modifier.type && m.modifierId !== modifier.modifierId,
                    );
                    let newModifiers = this.state.item.modifiers.filter(
                        m => m.type !== modifier.type,
                    );
                    currentModifiers.splice(newIndex, 0, modifier);
                    newModifiers = newModifiers.concat(currentModifiers);
                    this.setState({
                        item: {
                            ...this.state.item,
                            modifiers: newModifiers,
                        },
                    });
                }
            })
            .catch(this._handleRequestException);
    };

    _handleRequestException = e => {
        logger.warn(e);
        let errorMsg = strings.generalErrorMsg;
        if (
            e &&
            e.response &&
            e.response.status === 400 &&
            e.response.data &&
            typeof e.response.data === "string"
        ) {
            errorMsg = e.response.data;
        }
        if (this._isMounted) {
            this.setState({ loading: false, showAlertModal: true, errorMsg });
        }
    }

    _closeAlertModal = () => this.setState({ showAlertModal: false, errorMsg: "" });

    render() {
        if (this.state.loading) {
            return <Loader />;
        } else if (!this.state.item) {
            return <FailedToLoadAlert type={strings.item} />;
        }
        const item = this.state.item;
        const currentModifiers = item.modifiers.filter(
            m => m.type === this.state.currentModifierType,
        );
        return (
            <>
                <Grid>
                    <Row bsClass="m-t">
                        <Col md={6}>
                            <h4>{item.name}</h4>
                        </Col>
                        <Col md={6}>
                            {!this.state.item.isolatedImages128 ||
                            !this.state.item.isolatedImages128.length ? (
                                <AlertFlag label={strings.missingIsolatedImages128} />
                            ) : null}
                            {!this.state.item.isolatedImages352 ||
                            !this.state.item.isolatedImages352.length ? (
                                <AlertFlag label={strings.missingIsolatedImages352} />
                            ) : null}
                            {!this.state.item.isolatedImages1152 ||
                            !this.state.item.isolatedImages1152.length ? (
                                <AlertFlag label={strings.missingIsolatedImages1152} />
                            ) : null}
                        </Col>
                    </Row>
                    <Row bsClass="m-t">
                        <Col md={12}>
                            <div className={css(styles.tabWrapper)}>
                                <Tabs defaultActiveKey={1} id="uncontrolled-tab-example">
                                    <Tab eventKey={1} title={strings.info}>
                                        <ItemInfoTab
                                            key={item.id}
                                            initialItem={item}
                                            onSave={this._handleSaveInfo}
                                        />
                                    </Tab>
                                    <Tab eventKey={2} title={strings.images}>
                                        <Row>
                                            <Col md={4}>
                                                <ImageSetUploader
                                                    label={strings.isolatedImage128}
                                                    images={item ? item.isolatedImages128 : null}
                                                    onSubmit={this._handleUploadImage.bind(
                                                        this,
                                                        imageTypes.Isolated128,
                                                    )}
                                                    minWidth={128}
                                                    minHeight={92}
                                                />
                                            </Col>
                                            <Col md={4}>
                                                <ImageSetUploader
                                                    label={strings.isolatedImage352}
                                                    images={item ? item.isolatedImages352 : null}
                                                    onSubmit={this._handleUploadImage.bind(
                                                        this,
                                                        imageTypes.Isolated352,
                                                    )}
                                                    minWidth={352}
                                                    minHeight={253}
                                                />
                                            </Col>
                                            <Col md={4}>
                                                <ImageSetUploader
                                                    label={strings.isolatedImage1152}
                                                    images={item ? item.isolatedImages1152 : null}
                                                    onSubmit={this._handleUploadImage.bind(
                                                        this,
                                                        imageTypes.Isolated1152,
                                                    )}
                                                    minWidth={1152}
                                                    minHeight={828}
                                                />
                                            </Col>
                                        </Row>
                                    </Tab>
                                    <Tab eventKey={3} title={strings.modifiers}>
                                        <Col md={3}>
                                            <FormGroup controlId="formControlsSelect">
                                                <ControlLabel>{strings.type}</ControlLabel>
                                                <FormControl
                                                    componentClass="select"
                                                    value={this.state.currentModifierType}
                                                    onChange={this._handleModifierTypeChange}
                                                >
                                                    <option value={modifierTypes.required.value}>
                                                        {modifierTypes.required.label}
                                                    </option>
                                                    <option value={modifierTypes.ingredient.value}>
                                                        {modifierTypes.ingredient.label}
                                                    </option>
                                                    <option value={modifierTypes.addon.value}>
                                                        {modifierTypes.addon.label}
                                                    </option>
                                                </FormControl>
                                            </FormGroup>
                                        </Col>
                                        <Table striped bordered hover>
                                            <thead>
                                                <tr>
                                                    <th>{strings.name}</th>
                                                    <th>{strings.available}</th>
                                                    <th />
                                                    <th>{strings.sort}</th>
                                                    <th>{strings.manageSelections}</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {currentModifiers.map((m, i) => (
                                                    <tr key={m.modifierId}>
                                                        <td>{m.name}</td>
                                                        <td>
                                                            {`${
                                                                m.status === menuStatus.available.value
                                                                    ? strings.yes
                                                                    : strings.no
                                                            } `}
                                                        </td>
                                                        <td>
                                                            <Button
                                                                bsStyle={
                                                                    m.status ===
                                                                    menuStatus.available.value
                                                                        ? "danger"
                                                                        : "primary"
                                                                }
                                                                onClick={() =>
                                                                    this._toggleModifierStatus(m)
                                                                }
                                                            >
                                                                {m.status === menuStatus.available.value
                                                                    ? strings.disable
                                                                    : strings.enable}
                                                            </Button>
                                                        </td>
                                                        <td>
                                                            {
                                                                <div>
                                                                    {i === 0 ? null : (
                                                                        <Button
                                                                            onClick={() =>
                                                                                this._reorderModifier(
                                                                                    m,
                                                                                    i - 1,
                                                                                )
                                                                            }
                                                                        >
                                                                            &#9650;
                                                                        </Button>
                                                                    )}
                                                                    {i ===
                                                                    currentModifiers.length -
                                                                        1 ? null : (
                                                                        <Button
                                                                            onClick={() =>
                                                                                this._reorderModifier(
                                                                                    m,
                                                                                    i + 1,
                                                                                )
                                                                            }
                                                                        >
                                                                            &#9660;
                                                                        </Button>
                                                                    )}
                                                                </div>
                                                            }
                                                        </td>
                                                        <td>
                                                            <LinkButton
                                                                bsStyle="primary"
                                                                to={`/item/${item.id}/modifier/${m.modifierId}`}
                                                            >
                                                                {strings.edit}
                                                            </LinkButton>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </Table>
                                    </Tab>
                                </Tabs>
                            </div>
                        </Col>
                    </Row>
                </Grid>
                <AlertModal show={this.state.showAlertModal}
                    alertMessage={this.state.errorMsg}
                    handleClose={this._closeAlertModal}
                />
            </>
        );
    }
}
